import { useCallback, useMemo } from 'react'

import cn from 'classnames'

import { useTranslation } from 'react-i18next'

import { useParticipants } from '@app/components/Sidebar/components/Participants/useParticipants'
import { useConversationActorRole, useConversationDetails, useUser } from '@app/hooks'
import {
  trackFollowUpCallContactClick,
  trackFollowUpNotInterestedClick,
  trackFollowUpSendReminderClick,
} from '@app/services/tracking'
import { isBuyerView } from '@app/utils'

import { Icon } from '@lib/components'

import styles from './styles.module.scss'
import type { FollowUpActionsProps } from './types'

export const FollowUpActions = ({
  conversation,
  onCallContact,
  onPrefillReply,
  activeAction = null,
  onSelectAction,
}: FollowUpActionsProps) => {
  const { t } = useTranslation()
  const { fullName, phone, participant, listing } = useConversationDetails(
    conversation
  )
  const participants = useParticipants(conversation)
  const { currentUser } = useUser()
  const conversationActorRole = useConversationActorRole()
  const currentUserFullName = currentUser?.fullName?.trim() || ''
  const isBuyer = isBuyerView()

  const callParticipant = useMemo(() => {
    if (!participants.length) {
      return null
    }

    if (participant) {
      const matched = participants.find((item) => item.id === participant.id)
      if (matched) {
        return matched
      }
    }

    return participants.find((item) => !item.isCurrentUser) || null
  }, [participant, participants])

  const handleCallContact = useCallback(() => {
    trackFollowUpCallContactClick(
      conversation.id,
      listing,
      conversationActorRole
    )

    if (
      onPrefillReply &&
      (activeAction === 'sendReminder' || activeAction === 'notInterested')
    ) {
      onPrefillReply('')
    }

    if (!callParticipant || !onCallContact) {
      return
    }

    onSelectAction?.('callContact')
    onCallContact(callParticipant)
  }, [
    activeAction,
    callParticipant,
    conversation.id,
    listing,
    conversationActorRole,
    onCallContact,
    onPrefillReply,
    onSelectAction,
  ])

  const handleSendReminder = useCallback(() => {
    trackFollowUpSendReminderClick(
      conversation.id,
      listing,
      conversationActorRole
    )

    if (!onPrefillReply) {
      return
    }

    onSelectAction?.('sendReminder')

    const message = t('Chat.FollowUpActions.SendReminder.Message', {
      fullName: currentUserFullName,
    })

    onPrefillReply(message)
  }, [
    conversation.id,
    conversationActorRole,
    currentUserFullName,
    listing,
    onPrefillReply,
    onSelectAction,
    t,
  ])

  const handleNotInterested = useCallback(() => {
    trackFollowUpNotInterestedClick(
      conversation.id,
      listing,
      conversationActorRole
    )

    if (!onPrefillReply) {
      return
    }

    onSelectAction?.('notInterested')

    const message = t('Chat.FollowUpActions.NotInterested.Message', {
      fullName: currentUserFullName,
    })

    onPrefillReply(message)
  }, [
    conversation.id,
    conversationActorRole,
    currentUserFullName,
    listing,
    onPrefillReply,
    onSelectAction,
    t,
  ])

  const sendReminderClassName = useMemo(
    () =>
      cn(styles.actionButton, {
        [styles.actionButtonActive]: activeAction === 'sendReminder',
      }),
    [activeAction]
  )

  const notInterestedClassName = useMemo(
    () =>
      cn(styles.actionButton, {
        [styles.actionButtonActive]: activeAction === 'notInterested',
      }),
    [activeAction]
  )

  const callContactClassName = useMemo(
    () =>
      cn(styles.actionButton, {
        [styles.actionButtonActive]: activeAction === 'callContact',
      }),
    [activeAction]
  )

  const canCallContact =
    phone.length > 0 && !callParticipant?.isPrivateAdvertiser

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Icon path="icons/icon-ai" className={styles.icon} alt="" />
        {t('Chat.FollowUpActions.Title')}
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={sendReminderClassName}
          onClick={handleSendReminder}
          disabled={isBuyer || !onPrefillReply}
        >
          {t('Chat.FollowUpActions.SendReminder')}
        </button>
        {canCallContact && (
          <button
            type="button"
            className={callContactClassName}
            onClick={handleCallContact}
            disabled={isBuyer || !onCallContact}
          >
            {t('Chat.FollowUpActions.CallContact', { fullName })}
          </button>
        )}
        <button
          type="button"
          className={notInterestedClassName}
          onClick={handleNotInterested}
          disabled={isBuyer || !onPrefillReply}
        >
          {t('Chat.FollowUpActions.NotInterested')}
        </button>
      </div>
    </div>
  )
}
